import {
  BLOBS_STORE,
  openDatabase,
  request,
  toBuffer,
  transactionDone,
  UPLOADS_STORE,
} from '../persistence/idb.js';
import type { LocalBlobSource, PendingUpload, UploadQueueStore } from './upload-queue.js';

/** Upload queue that survives a browser restart, backed by the shared IndexedDB database. */
export class IndexedDbUploadQueueStore implements UploadQueueStore {
  private db: IDBDatabase | null = null;

  async open(): Promise<void> {
    this.db = await openDatabase();
  }

  private async requireDb(): Promise<IDBDatabase> {
    this.db ??= await openDatabase();
    return this.db;
  }

  async put(entry: PendingUpload): Promise<void> {
    const db = await this.requireDb();
    const tx = db.transaction(UPLOADS_STORE, 'readwrite');
    tx.objectStore(UPLOADS_STORE).put(entry, entry.blobId);
    await transactionDone(tx);
  }

  async remove(blobId: string): Promise<void> {
    const db = await this.requireDb();
    const tx = db.transaction(UPLOADS_STORE, 'readwrite');
    tx.objectStore(UPLOADS_STORE).delete(blobId);
    await transactionDone(tx);
  }

  async get(blobId: string): Promise<PendingUpload | null> {
    const db = await this.requireDb();
    const tx = db.transaction(UPLOADS_STORE, 'readonly');
    const value = await request<PendingUpload | undefined>(tx.objectStore(UPLOADS_STORE).get(blobId));
    await transactionDone(tx);
    return value ?? null;
  }

  async listReady(now: number): Promise<PendingUpload[]> {
    const db = await this.requireDb();
    const tx = db.transaction(UPLOADS_STORE, 'readonly');
    const all = await request<PendingUpload[]>(tx.objectStore(UPLOADS_STORE).getAll());
    await transactionDone(tx);
    return all.filter((entry) => entry.state !== 'uploaded' && entry.nextAttemptAt <= now);
  }
}

/**
 * Holds captured photo bytes until they reach R2.
 *
 * Keeping the original bytes locally is what allows a recipe photographed on a plane to
 * render immediately and upload later, and it is also the retry buffer: without it, a
 * failed upload would be unrecoverable because the CRDT only carries the hash.
 */
export class IndexedDbBlobSource implements LocalBlobSource {
  async write(localRef: string, bytes: Uint8Array): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(BLOBS_STORE, 'readwrite');
    tx.objectStore(BLOBS_STORE).put(toBuffer(bytes), localRef);
    await transactionDone(tx);
  }

  async read(localRef: string): Promise<Uint8Array> {
    const db = await openDatabase();
    const tx = db.transaction(BLOBS_STORE, 'readonly');
    const buffer = await request<ArrayBuffer | undefined>(tx.objectStore(BLOBS_STORE).get(localRef));
    await transactionDone(tx);
    if (buffer == null) throw new Error(`local photo bytes missing for ${localRef}`);
    return new Uint8Array(buffer);
  }

  async release(localRef: string): Promise<void> {
    const db = await openDatabase();
    const tx = db.transaction(BLOBS_STORE, 'readwrite');
    tx.objectStore(BLOBS_STORE).delete(localRef);
    await transactionDone(tx);
  }

  /** Object URL for rendering a photo that has not been uploaded yet. */
  async objectUrl(localRef: string): Promise<string> {
    const bytes = await this.read(localRef);
    return URL.createObjectURL(new Blob([toBuffer(bytes)]));
  }
}

export class MemoryUploadQueueStore implements UploadQueueStore {
  private readonly entries = new Map<string, PendingUpload>();

  async put(entry: PendingUpload): Promise<void> {
    this.entries.set(entry.blobId, entry);
  }

  async remove(blobId: string): Promise<void> {
    this.entries.delete(blobId);
  }

  async get(blobId: string): Promise<PendingUpload | null> {
    return this.entries.get(blobId) ?? null;
  }

  async listReady(now: number): Promise<PendingUpload[]> {
    return [...this.entries.values()].filter(
      (entry) => entry.state !== 'uploaded' && entry.nextAttemptAt <= now,
    );
  }
}

export class MemoryBlobSource implements LocalBlobSource {
  private readonly blobs = new Map<string, Uint8Array>();

  write(localRef: string, bytes: Uint8Array): void {
    this.blobs.set(localRef, bytes);
  }

  async read(localRef: string): Promise<Uint8Array> {
    const bytes = this.blobs.get(localRef);
    if (bytes == null) throw new Error(`local photo bytes missing for ${localRef}`);
    return bytes;
  }

  async release(localRef: string): Promise<void> {
    this.blobs.delete(localRef);
  }
}
